import React, { useEffect, useRef, useState } from 'react';

import { IconCloseCircleL } from '../../../icons';
import { FormattedMessage } from '../../../util/reactIntl';
import { composeValidators, isEntryWithoutSpecChars } from '../../../util/validators';
import { Button, FieldCheckbox, FieldTextInput, SecondaryButton } from '../../../components';
import css from '../EditUserInfoModal.css';
import sectionCss from './EditUserSkills.css';
import classNames from 'classnames';
import { skills_general as skillsOptionsGeneral } from '../../../marketplace-custom-config';
import { skills_experience as skillsExperienceOptions } from '../../../marketplace-custom-config';
import IconCheckbox from '../../../components/FieldCheckbox/IconCheckbox';
import { useBlurOnEnterKeyDown } from '../../../hooks/useBlurOnEnterKeyDown';

const SKILLS_PRIMARY = skillsOptionsGeneral.map(({ label }) => label);
const SKILLS_EXPERIENCE = skillsExperienceOptions.map(({ label }) => label);

const SKILLS_TAB_PRIMARY = 'primary';
const SKILLS_TAB_EXPERIENCE_BASED = 'experienceBased';

const EditUserSkillsContent = ({
    intl,
    values,
    form,
    updateInProgress,
    uploadDocumentsInProgress,
    // pristine,
    initialValues,
    parentOnSubmit,
    errors,
}) => {
    const [skillsEntities, setSkillsEntities] = useState([]);
    const [skillTab, setSkillTab] = useState(SKILLS_TAB_PRIMARY);

    const {
        skillsGeneral: initialPrimarySkills,
        skillsExperience: initialExperienceBasedSkills,
    } = initialValues;

    const isPrimaryTab = skillTab === SKILLS_TAB_PRIMARY;
    const skillsOptions = isPrimaryTab ? SKILLS_PRIMARY : SKILLS_EXPERIENCE;
    const initialSkills = isPrimaryTab ? initialPrimarySkills : initialExperienceBasedSkills;
    const skills = isPrimaryTab ? values.skillsGeneral : values.skillsExperience;

    const fieldNameCustomSkill = isPrimaryTab ? 'customSkill' : 'customSkillExperienceBased';
    const fieldNameSkills = isPrimaryTab ? 'skillsGeneral' : 'skillsExperience';

    const { customSkill, customSkillExperienceBased } = values;
    const { getFieldState, change: changeFormField } = form;
    const { customSkill: customSkillInvalid } = errors || {};
    const { value: customValue, blur: blurCustomField, change: changeCustomValue } =
        getFieldState(fieldNameCustomSkill) || {};

    const noSkills = !skills || skills.length === 0;
    const hasValue = !!customValue;
    const disabled =
        // pristine ||
        uploadDocumentsInProgress || !!customSkill || !!customSkillExperienceBased || noSkills;

    const noSpecCharsMessage = intl.formatMessage({
        id: 'ProfileSettingsForm.noSpecCharsMessage',
    });

    const fieldAlreadyCreated = intl.formatMessage({
        id: 'ProfileSettingsForm.fieldAlreadyCreated',
    });

    useEffect(() => {
        setSkillsEntities(
            Object.values(
                [...skillsOptions, ...(skills || [])].reduce(
                    (acc, item) => ({ ...acc, [item]: item }),
                    {}
                )
            )
                .reduce((acc, label) => {
                    const isCustom = !skillsOptions.some(s => s === label);
                    return [...acc, { label, isCustom }];
                }, [])
                .sort((_, { isCustom }) => (isCustom ? -1 : 0))
        );
    }, [skillTab]);

    const handleBlur = () => {
        blurCustomField();

        setTimeout(() => {
            const { valid, value } = getFieldState(fieldNameCustomSkill) || {};

            if (!valid || !value) {
                return;
            }

            changeFormField(fieldNameSkills, skills ? [...skills, value] : [value]);

            setSkillsEntities([...skillsEntities, { label: value, isCustom: true }]);

            changeCustomValue('');
            form.resetFieldState(fieldNameCustomSkill);
        }, 250);
    };

    const removeDiscipline = value => {
        changeFormField(
            fieldNameSkills,
            (skills || []).filter(s => s !== value)
        );
        setSkillsEntities(skillsEntities.filter(({ label }) => label !== value));
    };

    const customItemAlreadyCreated = message => value =>
        (skills || []).some(s => s.toLowerCase() === (value || '').toLowerCase())
            ? message
            : undefined;

    const fieldIsRequired = message => value =>
        !value || !(value || '').trim() ? message : undefined;

    const customFieldValidators = composeValidators(
        fieldIsRequired('Dieses Feld wird benötigt'),
        isEntryWithoutSpecChars(noSpecCharsMessage),
        customItemAlreadyCreated(fieldAlreadyCreated)
    );

    const inputRef = useBlurOnEnterKeyDown({
        form,
        fieldName: fieldNameCustomSkill,
    });

    return (
        <>
            <section
                className={classNames(sectionCss.skillsHolder, {
                    [sectionCss.skillsHolderWithError]: customSkillInvalid,
                })}
            >
                <nav className={sectionCss.skillTabs}>
                    <SecondaryButton
                        onClick={() => setSkillTab(SKILLS_TAB_PRIMARY)}
                        className={classNames(sectionCss.skillTab, {
                            [sectionCss.activeSkillTab]: isPrimaryTab,
                        })}
                    >
                        Allgemein
                    </SecondaryButton>
                    <SecondaryButton
                        onClick={() => setSkillTab(SKILLS_TAB_EXPERIENCE_BASED)}
                        className={classNames(sectionCss.skillTab, {
                            [sectionCss.activeSkillTab]: !isPrimaryTab,
                        })}
                    >
                        Umgang
                    </SecondaryButton>
                </nav>
                <p>{isPrimaryTab ? 'Das zeichnet mich aus:' : 'Ich habe Erfahrung mit:'}</p>
                {skillsEntities.map(({ label, isCustom }) => (
                    <FieldCheckbox
                        type="checkbox"
                        key={label}
                        form={form}
                        name={fieldNameSkills}
                        id={label}
                        label={label}
                        value={label}
                        textClassName={sectionCss.skillText}
                    >
                        {isCustom && (
                            <IconCloseCircleL
                                rootClassName={sectionCss.removeCustomIcon}
                                clickHandler={() => removeDiscipline(label)}
                            />
                        )}
                    </FieldCheckbox>
                ))}
                <div className={sectionCss.customSkillOptionHolder}>
                    <IconCheckbox />
                    {!hasValue && <p>Stärke hinzufügen</p>}
                    <FieldTextInput
                        rootClassName={sectionCss.customSkillOption}
                        type="text"
                        id={fieldNameCustomSkill}
                        name={fieldNameCustomSkill}
                        inputRef={inputRef}
                        onBlur={handleBlur}
                        validate={customFieldValidators}
                        placeholder="Stärke hinzufügen"
                    />
                </div>
            </section>
            <div
                className={classNames(css.userDataProtectedMessage, sectionCss.preSkillsOverlay)}
            />
            <footer>
                <Button
                    disabled={disabled}
                    onClick={() => {
                        const prepareForSubmit = (skillsToSubmit, skillsOptionsForSubmit) =>
                            (skillsToSubmit || []).sort((primary, secondary) => {
                                const primaryIndex = skillsOptionsForSubmit.indexOf(primary);
                                const secondaryIndex = skillsOptionsForSubmit.indexOf(secondary);

                                return (
                                    (primaryIndex === -1 ? Infinity : primaryIndex) -
                                    (secondaryIndex === -1 ? Infinity : secondaryIndex)
                                );
                            });

                        return parentOnSubmit({
                            skillsGeneral: prepareForSubmit(values.skillsGeneral, SKILLS_PRIMARY),
                            skillsExperience: prepareForSubmit(
                                values.skillsExperience,
                                SKILLS_EXPERIENCE
                            ),
                        });
                    }}
                    inProgress={updateInProgress || uploadDocumentsInProgress}
                >
                    <FormattedMessage
                        id={`ProfileSettingsForm.editUserInfoAction-${
                            initialSkills ? 'edit' : 'userSkills'
                        }`}
                    />
                </Button>
            </footer>
        </>
    );
};

export default EditUserSkillsContent;
